"use client";
import { useEffect, useRef } from "react";
import Link from "next/link";
import {
  ArrowUpRight,
  BadgeCheck,
  CalendarDays,
  MapPin,
  Sparkles,
  Star,
} from "lucide-react";
import { AjaxPostAnon } from "@/util/Ajax";
import { DiscoverItem, webUrl } from "@/lib/discover";
import styles from "./discover.module.css";

export default function ListingCards({ items }: { items: DiscoverItem[] }) {
  const root = useRef<HTMLDivElement>(null);
  const tracked = useRef(new Set<string>());
  useEffect(() => {
    if (!root.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = (entry.target as HTMLElement).dataset.listingId;
          if (!entry.isIntersecting || !id || tracked.current.has(id)) return;
          tracked.current.add(id);
          void AjaxPostAnon("Discover/Analytics", {
            encListingId: id,
            eventType: "Impression",
            referrerUrl: window.location.href,
          });
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 },
    );
    root.current
      .querySelectorAll("[data-listing-id]")
      .forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [items]);
  return (
    <div className={styles.grid} ref={root}>
      {items.map((item) => {
        const imageUrl = webUrl(item.imageUrl);
        const reviewCount = Math.max(0, Number(item.reviewCount) || 0);
        const averageRating = Math.min(
          5,
          Math.max(0, Number(item.averageRating) || 0),
        );
        return (
          <Link
            key={item.encryptedId}
            className={styles.card}
            href={`/discover/${encodeURIComponent(item.slug || item.encryptedId)}`}
            data-listing-id={item.encryptedId}
            aria-label={`Explore ${item.title}`}
          >
            <div className={styles.cardImage}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={`${item.title} business`}
                  width={420}
                  height={220}
                  loading="lazy"
                  decoding="async"
                  className="!object-contain"
                />
              ) : (
                <span aria-hidden="true">
                  {item.title.slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className={styles.imageBadge}>{item.industry}</span>
              {item.isVerified && (
                <span className={styles.verifiedBadge}>
                  <BadgeCheck size={14} aria-hidden="true" />
                  Verified
                </span>
              )}
            </div>
            <article className={styles.cardBody}>
              <div className={styles.cardTitleRow}>
                <h2>{item.title}</h2>
                {reviewCount > 0 && (
                  <span
                    className={styles.rating}
                    aria-label={`${averageRating.toFixed(1)} out of 5 from ${reviewCount} reviews`}
                  >
                    <Star size={13} fill="currentColor" aria-hidden="true" />
                    {averageRating.toFixed(1)} <small>({reviewCount})</small>
                  </span>
                )}
              </div>
              <p className={styles.location}>
                <MapPin size={14} aria-hidden="true" />
                {item.location ||
                  (item.isRemote ? "Available remotely" : "South Africa")}
                {item.isRemote && item.location ? " · Remote services" : ""}
              </p>
              <p className={`${styles.muted} ${styles.clamp}`}>
                {item.description ||
                  `Explore ${item.title} and its ${item.industry.toLowerCase()} business profile.`}
              </p>
              <div className={styles.cardSignals}>
                {item.acceptsBookings && (
                  <span>
                    <CalendarDays size={13} aria-hidden="true" />
                    Bookings available
                  </span>
                )}
                {item.hasActiveSpecial && (
                  <span>
                    <Sparkles size={13} aria-hidden="true" />
                    Special offer
                  </span>
                )}
              </div>
              <div className={styles.cardFooter}>
                <span
                  className={
                    item.isOpenNow ? styles.openStatus : styles.profileStatus
                  }
                >
                  {item.isOpenNow
                    ? "Open now"
                    : reviewCount
                      ? `${reviewCount} customer ${reviewCount === 1 ? "review" : "reviews"}`
                      : "View business profile"}
                </span>
                <span className={styles.exploreLink}>
                  Explore business
                  <ArrowUpRight size={16} aria-hidden="true" />
                </span>
              </div>
            </article>
          </Link>
        );
      })}
    </div>
  );
}
